import { useEffect } from 'react';
import axios from 'axios';
import { useAuthStore, type AuthUser } from './auth-store';

/**
 * Run once on app mount: try to silently exchange the HttpOnly refresh cookie
 * for an access token + user. If it works, the session is restored; if not,
 * the user is left unauthenticated and `RequireAuth` redirects to /login.
 *
 * Uses a raw axios call (not `api`) to avoid the 401-retry interceptor loop:
 * the bootstrap *is* the refresh, not a request that needs refreshing.
 */
const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

type RefreshResponse = {
  data: {
    accessToken: string;
    expiresIn: number;
    user: AuthUser;
  };
};

export function useBootstrapAuth(): boolean {
  const bootstrapped = useAuthStore((s) => s.bootstrapped);
  const setSession = useAuthStore((s) => s.setSession);
  const setBootstrapped = useAuthStore((s) => s.setBootstrapped);

  useEffect(() => {
    if (bootstrapped) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post<RefreshResponse>(
          `${BASE_URL}/v1/auth/refresh`,
          {},
          { withCredentials: true },
        );
        if (cancelled) return;
        setSession(res.data.data.accessToken, res.data.data.user);
      } catch {
        // No cookie / expired chain / network error → stay logged out.
      } finally {
        if (!cancelled) setBootstrapped(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bootstrapped, setSession, setBootstrapped]);

  return bootstrapped;
}
